import { Sentry, sentryEnabled } from "@/lib/sentry";

/**
 * Dynamic-import ("chunk load") failures.
 *
 * These happen when a user has an old index.html open after a deploy and the
 * hashed chunk it references no longer exists. They are user-visible (blank
 * area / missing widget) so they MUST be reported — but with their own
 * fingerprint + tag so alerting can treat them as a deploy-health signal
 * instead of an application bug.
 */
const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk/i,
  /ChunkLoadError/i,
];

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === "string"
        ? error
        : "";
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

const RELOAD_KEY = "baddy_chunk_reload_at";
const RELOAD_COOLDOWN_MS = 60_000;

/**
 * Report a failed dynamic import to Sentry as a distinct, alertable issue.
 * `component` is the logical chunk owner (e.g. "SupportChatWidget").
 */
export function reportChunkError(
  error: unknown,
  component: string,
  extra?: Record<string, unknown>,
) {
  const err = error instanceof Error ? error : new Error(String(error));
  console.warn(`[chunk-load-failed] ${component}:`, err.message);
  if (!sentryEnabled) return;

  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setTag("error_class", "chunk_load_failure");
    scope.setTag("chunk_component", component);
    scope.setTag("app_version", (import.meta.env.VITE_APP_VERSION as string) || "unknown");
    scope.setFingerprint(["chunk-load-failure", component]);
    scope.setContext("chunk", {
      component,
      url: window.location.href,
      online: navigator.onLine,
      ...extra,
    });
    Sentry.captureException(err);
  });
}

/**
 * Best-effort self-heal: one reload per minute so a stale index.html picks up
 * the new asset manifest. Never loops.
 */
export function maybeReloadForStaleChunks(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

/** Catch chunk failures that escape React (router prefetch, bare imports). */
export function installChunkErrorListeners() {
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      reportChunkError(event.reason, "unhandled_dynamic_import", {
        source: "unhandledrejection",
      });
    }
  });

  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.error ?? event.message)) {
      reportChunkError(event.error ?? event.message, "window_error", {
        source: "window.onerror",
        filename: event.filename,
      });
    }
  });
}
